import { useMemo } from "react";
import { Task } from "./useTasks";
import { createUniversalSearchMatcher } from "@/utils/searchUtils";

export function useTasksFiltering(
  tasks: Task[],
  searchQuery: string,
  statusFilter: string[],
  priorityFilter: string[],
  maxDaysAgo: number | null,
  showCancelled: boolean = false,
  ownershipFilter?: 'my-tasks' | 'assigned-to-me' | 'all',
  currentUserId?: string | null
) {
  // Filter and sort tasks
  const filteredTasks = useMemo(() => {
    const lowerStatusFilter = statusFilter.map(s => s.toLowerCase());
    const lowerPriorityFilter = priorityFilter.map(p => p.toLowerCase());
    
    // Create universal search matcher for task search
    const searchMatcher = createUniversalSearchMatcher(searchQuery);
    
    // Filter by search query, status, priority, days ago, and ownership
    const filtered = tasks.filter(task => {
      // Search term filter - universal search with word-order independence
      const searchableText = [
        task.name,
        task.description || '',
        task.assignee?.first_name || '',
        task.assignee?.last_name || '',
        task.assigned_to || ''
      ].filter(field => field && field.trim().length > 0).join(' ');
      
      const matchesSearch = searchMatcher(searchableText);
      
      // Status filter - ensure lowercase comparison
      // If task is cancelled, only check showCancelled flag, not the status filter
      const matchesStatus = task.status === 'cancelled'
        ? showCancelled
        : (statusFilter.length === 0 
          ? true
          : (task.status && lowerStatusFilter.includes(task.status.toLowerCase())));
      
      // Priority filter - ensure lowercase comparison
      const matchesPriority = 
        priorityFilter.length === 0 || 
        (task.priority && lowerPriorityFilter.includes(task.priority.toLowerCase()));
      
      // Days ago filter - filter by creation date, but keep overdue tasks if not done
      const matchesDaysAgo = 
        maxDaysAgo === null || (() => {
          if (!task.created_at) return false;
          const created = new Date(task.created_at);
          const now = new Date();
          const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
          
          // If task is within the days limit, show it
          if (diffDays <= maxDaysAgo) return true;
          
          // If task is overdue and not done, show it regardless of age
          if (task.due_date && task.status !== 'done') {
            const dueDate = new Date(task.due_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time to start of day
            if (dueDate < today) return true;
          }
          
          return false;
        })();
      
      // Ownership filter - only apply if specified and currentUserId is available
      const matchesOwnership = !ownershipFilter || !currentUserId || (() => {
        if (ownershipFilter === 'my-tasks')
          // Only tasks created by me
          return task.user_id === currentUserId;
        
        if (ownershipFilter === 'assigned-to-me')
          // Only tasks assigned to me
          return task.assigned_to === currentUserId;
        
        // 'all' - show all tasks (no filtering)
        return true;
      })();
      
      return matchesSearch && matchesStatus && matchesPriority && matchesDaysAgo && matchesOwnership;
    });
    
    return filtered;
  }, [tasks, searchQuery, statusFilter, priorityFilter, maxDaysAgo, showCancelled, ownershipFilter, currentUserId]);

  return {
    filteredTasks,
    totalCount: filteredTasks.length
  };
}
